import type { MsgKey } from "./i18n";
// 显式带 .ts —— node 的 ESM 解析不做扩展名补全，不写就跑不了单元测试。
// vite 两种都能解析。
import { getToken } from "./bridge.ts";

/**
 * 后端连通性探测。
 *
 * 没有这一层时，管理 API 挂掉的表现是「页面正常渲染、只是没有数据」——
 * 策划会以为配置被清空了，而不是后端不可达。这跟白屏是同一类问题：
 * **出错了但没人说**。
 *
 * 探测打 /admin/api/health，它不查库、不鉴权，纯粹回答「Worker 活着吗」。
 */
export type Health =
  | { state: "checking" }
  | { state: "ok"; env: string }
  | { state: "down"; reason: { key: MsgKey; vars?: Record<string, string | number> } };

/** 超时上限。Workers 冷启动是毫秒级，5s 还没回就是真出问题了 */
const TIMEOUT_MS = 5_000;

export async function probeHealth(): Promise<Health> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
  try {
    const token = getToken();
    const res = await fetch("/admin/api/health", {
      signal: ctl.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) return { state: "down", reason: { key: "h.http" as const, vars: { status: res.status } } };

    const body = (await res.json()) as { code?: string; data?: { env?: string } };
    // 沿用全局信封 {code, message, data}，code 为字符串，"0" 表示成功
    if (body.code !== "0") {
      return { state: "down", reason: { key: "h.bizCode" as const, vars: { code: body.code ?? "?" } } };
    }
    return { state: "ok", env: body.data?.env ?? "unknown" };
  } catch (e) {
    // AbortError 与网络错误分开报——前者是慢，后者是断，排查方向不同
    const err = e as Error;
    return {
      state: "down",
      reason: err.name === "AbortError" ? { key: "h.timeout" as const, vars: { s: TIMEOUT_MS / 1000 } } : { key: "h.unreachable" as const },
    };
  } finally {
    clearTimeout(timer);
  }
}
